// Learning Zone games speak through the browser's speechSynthesis, and none
// of them pick a voice - so they got the OS/browser default, which is a male
// voice on most setups (e.g. "Microsoft David" on Windows). Rather than set
// a voice in every game's many `speak` calls, this wraps speechSynthesis.speak
// once: any utterance that doesn't already have a voice gets the best
// available female English voice. Import it for its side effect.

// Known female voices across Chrome, Edge, Safari/macOS/iOS and Android,
// best first.
const PREFERRED_FEMALE = [
   'Microsoft Aria',
   'Microsoft Jenny',
   'Microsoft Ana',
   'Microsoft Zira',
   'Google US English',
   'Google UK English Female',
   'Samantha',
   'Karen',
   'Moira',
   'Tessa',
   'Victoria',
   'Serena',
   'Fiona',
   'Allison',
   'Ava',
   'Susan',
   'Hazel',
   'Heera',
   'Veena',
];

const MALE_HINT = /\b(male|david|mark|guy|daniel|alex|fred|george|james|ryan|christopher|eric|roger|steffan|ravi|thomas|oliver|rishi|aaron|arthur|gordon|lee|reed|rocko|grandpa|junior|ralph|bruce)\b/i;

let cachedVoice;

const pickFemaleVoice = (synth) => {
   const voices = synth.getVoices() || [];
   if (!voices.length) return null;
   const english = voices.filter((v) => /^en([-_]|$)/i.test(v.lang || ''));

   for (const name of PREFERRED_FEMALE) {
      const match = english.find((v) => v.name.includes(name)) || voices.find((v) => v.name.includes(name));
      if (match && !/\bmale\b/i.test(match.name)) return match;
   }
   // Explicitly labelled female, then any English voice that doesn't look male.
   return english.find((v) => /female/i.test(v.name))
      || english.find((v) => !MALE_HINT.test(v.name))
      || null;
};

const getFemaleVoice = (synth) => {
   if (cachedVoice === undefined || cachedVoice === null) {
      cachedVoice = pickFemaleVoice(synth);
   }
   return cachedVoice;
};

const install = () => {
   if (typeof window === 'undefined') return;
   const synth = window.speechSynthesis;
   if (!synth || synth.__femaleVoiceInstalled) return;
   synth.__femaleVoiceInstalled = true;

   const originalSpeak = synth.speak.bind(synth);
   const originalCancel = synth.cancel.bind(synth);
   // Chrome loads its voice list asynchronously, so the very first line a
   // game speaks can arrive before any voice exists. Hold those briefly
   // until the list lands (or give up and use the default after 1s).
   let pending = [];
   let pendingTimer = null;

   const flushPending = () => {
      clearTimeout(pendingTimer);
      pendingTimer = null;
      const queued = pending;
      pending = [];
      const voice = getFemaleVoice(synth);
      queued.forEach((u) => {
         if (voice && !u.voice) {
            u.voice = voice;
            u.lang = voice.lang;
         }
         originalSpeak(u);
      });
   };

   synth.addEventListener?.('voiceschanged', () => {
      cachedVoice = pickFemaleVoice(synth);
      if (pending.length) flushPending();
   });

   synth.speak = (utterance) => {
      if (!utterance || utterance.voice) {
         originalSpeak(utterance);
         return;
      }
      const voice = getFemaleVoice(synth);
      if (voice) {
         utterance.voice = voice;
         utterance.lang = voice.lang;
         originalSpeak(utterance);
         return;
      }
      if ((synth.getVoices() || []).length) {
         // Voices are loaded but none is female - use the default.
         originalSpeak(utterance);
         return;
      }
      pending.push(utterance);
      if (!pendingTimer) pendingTimer = setTimeout(flushPending, 1000);
   };

   // Games cancel before speaking a new line - drop anything still held.
   synth.cancel = () => {
      pending = [];
      clearTimeout(pendingTimer);
      pendingTimer = null;
      originalCancel();
   };

   // Kick off voice loading early.
   synth.getVoices();
};

install();

export default install;
